import { useState, useEffect, useRef, useCallback } from "react";

const REVIEWS = [
  {
    author: "Boligejer i Odense",
    location: "Odense NØ",
    rating: 5,
    text: "Morten rykkede hurtigt ud da vores afløb stoppede til en lørdag formiddag. Super professionel, forklarede præcis hvad problemet var og fik det løst med det samme. Kan varmt anbefales!",
    service: "Akut afløbsproblem"
  },
  {
    author: "Grundejerforening",
    location: "Agedrup",
    rating: 5,
    text: "Fyns Afløbsteknik monterede rottespærre i vores brønde. Certificeret arbejde, overholdt alle aftaler og priser. Trygt at vide man bruger en autoriseret fagmand.",
    service: "Rottespærre montering"
  },
  {
    author: "Husejer",
    location: "Kerteminde",
    rating: 5,
    text: "Fik installeret højvandslukke i kælderen for at undgå oversvømmelse ved skybrud. Grundig vejledning hele vejen igennem og ordentligt opryddet bagefter.",
    service: "Højvandslukke"
  },
  {
    author: "Erhvervskunde",
    location: "Nyborg",
    rating: 5,
    text: "Brugte Fyns Afløbsteknik til TV-inspektion og efterfølgende punktreparation uden opgravning. Sparede os for en kæmpe graveudgift i indkørslen.",
    service: "Kloak TV & punktreparation"
  }
];

export default function ReviewsSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [visibleCount, setVisibleCount] = useState(3);
  const timerRef = useRef(null);

  // Responsive items per view: desktop 3, tablet 2, mobile 1
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleCount(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const total = REVIEWS.length;

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % total);
  }, [total]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  // Autoplay
  useEffect(() => {
    timerRef.current = setInterval(nextSlide, 5000);
    return () => clearInterval(timerRef.current);
  }, [nextSlide]);

  const handleTouchStart = (e) => {
    clearInterval(timerRef.current);
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    setTouchStart(null);
    timerRef.current = setInterval(nextSlide, 5000);
  };

  // Build visible items slice in infinite wrap
  const visibleReviews = [];
  for (let i = 0; i < visibleCount; i++) {
    const index = (currentIndex + i) % total;
    visibleReviews.push({ ...REVIEWS[index], originalIndex: index });
  }

  return (
    <div
      className="relative select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => clearInterval(timerRef.current)}
      onMouseLeave={() => {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(nextSlide, 5000);
      }}
    >
      {/* Slider Grid */}
      <div className={`grid gap-5 ${visibleCount === 1 ? "grid-cols-1" : visibleCount === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
        {visibleReviews.map((rev, idx) => (
          <div
            key={`${rev.originalIndex}-${idx}`}
            className="bg-[#111620] border border-neutral-800 rounded-xl p-6 flex flex-col justify-between shadow-lg"
          >
            <div>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex text-amber-400 text-sm">
                  {[...Array(rev.rating)].map((_, rIdx) => (
                    <i key={rIdx} className="fa-solid fa-star" aria-hidden="true" />
                  ))}
                </div>
                <span className="text-xs bg-neutral-800 text-neutral-300 font-semibold px-2 py-0.5 rounded">
                  {rev.service}
                </span>
              </div>

              <p className="text-sm text-neutral-300 italic mb-6 leading-relaxed">
                "{rev.text}"
              </p>
            </div>

            <div className="pt-4 border-t border-neutral-800/80 flex items-center justify-between">
              <div>
                <span className="font-bold text-white text-sm block">
                  {rev.author}
                </span>
                <span className="text-xs text-neutral-400">
                  {rev.location}
                </span>
              </div>
              <i className="fa-solid fa-circle-check text-neutral-400 text-sm" title="Verificeret kunde" aria-hidden="true" />
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-center gap-3 mt-8">
        <button
          type="button"
          onClick={prevSlide}
          className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-neutral-700 text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-400"
          aria-label="Forrige anmeldelse"
        >
          <i className="fa-solid fa-chevron-left" aria-hidden="true" />
        </button>

        <div className="flex items-center gap-1.5 px-2">
          {REVIEWS.map((_, dotIdx) => (
            <button
              key={dotIdx}
              type="button"
              onClick={() => setCurrentIndex(dotIdx)}
              className={`h-2 rounded-full transition-all ${
                dotIdx === currentIndex ? "w-6 bg-neutral-300" : "w-2 bg-neutral-700 hover:bg-neutral-600"
              }`}
              aria-label={`Gå til anmeldelse ${dotIdx + 1}`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={nextSlide}
          className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-neutral-700 text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-400"
          aria-label="Næste anmeldelse"
        >
          <i className="fa-solid fa-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
